import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { valorPorExtenso } from "@/lib/extenso";
import { loadImageDataUrl } from "@/components/CompanyInfoBanner";
import { parseNum } from "@/lib/utils";
import { toast } from "sonner";
import { Eraser, FilePdf, WhatsappLogo, Envelope } from "@phosphor-icons/react";
import jsPDF from "jspdf";

const latin = (t) => String(t || "").replace(/→/g, "-").replace(/[–—]/g, "-").replace(/[^\x00-\xFF]/g, "");
const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function SignPad({ padRef }) {
  const drawing = useRef(false);
  useEffect(() => {
    const c = padRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio; c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d"); ctx.scale(ratio, ratio); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
  }, [padRef]);
  const pos = (e) => { const r = padRef.current.getBoundingClientRect(); const t = e.touches?.[0] || e; return [t.clientX - r.left, t.clientY - r.top]; };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = padRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); padRef.current.dataset.signed = "1"; };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = padRef.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); padRef.current.dataset.stroke = String(Date.now()); };
  const end = () => { drawing.current = false; };
  return (
    <canvas ref={padRef} data-testid="receipt-sign-pad" className="w-full h-32 border-2 border-dashed rounded-md bg-white touch-none"
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
  );
}

export const receiptLink = (token) => `${API}/public/receipt/${token}`;
export const receiptShareText = (r) => `*Recibo — Viagem ${r.viagem_number}*\nValor: ${fmt(r.value)}\nContratante: ${r.contractor_name || "-"}\n\nPDF: ${receiptLink(r.share_token)}`;

export function ReceiptDialog({ group, data, onClose, onSaved }) {
  const pad = useRef(null);
  const saved = useRef(null);
  const [value, setValue] = useState("");
  const [desc, setDesc] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!group) return;
    setValue(group.total.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    const fretes = group.fretes.map((f) => `F${f.frete_number || "?"} ${f.route.replace("→", "-")}`).join("; ");
    setDesc(`serviços de transporte rodoviário de cargas referentes à Viagem Nº ${data.viagem?.viagem_number} (${fretes})${group.detention ? `, incluindo estadia de ${fmt(group.detention)}` : ""}`);
    setCity(data.company?.city || "");
    saved.current = null;
  }, [group, data]);

  const build = async () => {
    const v = parseNum(value);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210, M = 18;
    let y = 20;
    if (data.company?.logo_file_id) {
      const img = await loadImageDataUrl(data.company.logo_file_id);
      if (img) { doc.addImage(img.url, img.format, M, y - 6, Math.min(50, 18 * img.ratio), 18); y += 18; }
    }
    doc.setFont(undefined, "bold"); doc.setFontSize(20); doc.text("RECIBO", W / 2, y, { align: "center" });
    doc.setFontSize(14); doc.text(`Valor: ${fmt(v)}`, W - M, y + 10, { align: "right" }); y += 26;
    doc.setFont(undefined, "normal"); doc.setFontSize(11);
    const prestador = `${data.company?.name || ""}${data.company?.cnpj ? `, CNPJ/CPF ${data.company.cnpj}` : ""}`;
    const contratante = `${group.name || "________________"}${group.cnpj ? `, CNPJ ${group.cnpj}` : ""}`;
    const body = latin(`Eu, ${prestador}, declaro que recebi de ${contratante} a importância de ${fmt(v)} (${valorPorExtenso(v)}), referente a ${desc}.`);
    const lines = doc.splitTextToSize(body, W - 2 * M);
    doc.text(lines, M, y); y += lines.length * 6 + 6;
    doc.text("Para clareza, firmo o presente recibo, dando plena quitação do valor acima.", M, y); y += 14;
    const hoje = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
    doc.text(latin(`${city ? city + ", " : ""}${hoje}.`), W - M, y, { align: "right" }); y += 26;
    if (pad.current?.dataset.signed) doc.addImage(pad.current.toDataURL("image/png"), "PNG", M + 10, y - 22, 70, 22);
    doc.line(M, y, M + 85, y); doc.line(W - M - 85, y, W - M, y);
    doc.setFontSize(9);
    doc.text(["Prestador", latin(data.company?.name), data.company?.cnpj ? `CNPJ/CPF ${data.company.cnpj}` : ""], M, y + 5);
    doc.text(["Contratante", latin(group.name), group.cnpj ? `CNPJ ${group.cnpj}` : ""], W - M - 85, y + 5);
    doc.setFontSize(7); doc.setTextColor(120); doc.text("Gerado por Acerto Rápido JVO · acertorapidojvo.app", W / 2, 287, { align: "center" });
    const name = `Recibo-Viagem-${data.viagem?.viagem_number}-${(group.name || "contratante").replace(/\s+/g, "_").slice(0, 30)}.pdf`;
    return { doc, blob: doc.output("blob"), name, v };
  };

  const persist = async (b) => {
    const key = [value, desc, city, pad.current?.dataset.signed || "", pad.current?.dataset.stroke || ""].join("|");
    if (saved.current?.key === key) return saved.current.rec;
    const fd = new FormData(); fd.append("file", b.blob, b.name);
    const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const { data: rec } = await api.post("/receipts", {
      viagem_id: data.viagem.id, file_id: up.data.id, contractor_name: group.name || "", contractor_cnpj: group.cnpj || "",
      value: b.v, description: desc, city, signed: !!pad.current?.dataset.signed,
    });
    saved.current = { key, rec };
    onSaved?.();
    return rec;
  };

  const run = async (channel) => {
    setBusy(true);
    try {
      const b = await build();
      if (!(b.v > 0)) { toast.error("Informe um valor maior que zero"); return; }
      const rec = await persist(b);
      if (channel === "pdf") { b.doc.save(b.name); toast.success("Recibo salvo no histórico"); return; }
      const file = new File([b.blob], b.name, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ title: "Recibo", files: [file] }); return; } catch { /* cancelado */ }
      }
      const text = receiptShareText(rec);
      if (channel === "whatsapp") window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
      else window.location.href = `mailto:?subject=${encodeURIComponent(`Recibo — Viagem ${data.viagem?.viagem_number}`)}&body=${encodeURIComponent(text)}`;
    } catch (e) { toast.error("Erro ao gerar o recibo"); console.error(e); } finally { setBusy(false); }
  };

  const clear = () => { const c = pad.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); delete c.dataset.signed; };
  const v = parseNum(value);
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} data-testid="receipt-dialog">
        <DialogHeader><DialogTitle>Recibo de prestação de serviço</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-slate-50 border p-3">
            <div><span className="text-slate-500">Contratante:</span> <b data-testid="receipt-contractor">{group?.name || "Não informada"}</b>{group?.cnpj ? ` · CNPJ ${group.cnpj}` : ""}</div>
            <div><span className="text-slate-500">Prestador:</span> <b>{data.company?.name}</b>{data.company?.cnpj ? ` · ${data.company.cnpj}` : ""}</div>
          </div>
          <div><Label>Valor (R$)</Label><Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} data-testid="receipt-value" />
            <p className="text-xs text-slate-500 mt-1" data-testid="receipt-extenso">{valorPorExtenso(v)}</p></div>
          <div><Label>Referente a</Label><textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="w-full border rounded-md p-2 text-sm" data-testid="receipt-desc" /></div>
          <div><Label>Cidade</Label><Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="receipt-city" /></div>
          <div>
            <div className="flex justify-between items-center"><Label>Sua assinatura (com o dedo)</Label><button type="button" onClick={clear} className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="btn-receipt-clear"><Eraser size={12} /> Limpar</button></div>
            <SignPad padRef={pad} />
            <p className="text-xs text-slate-500 mt-1">A contratante assina no espaço ao lado, no papel ou no PDF.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
            <Button disabled={busy} onClick={() => run("pdf")} data-testid="btn-receipt-pdf" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"><FilePdf size={16} /> Baixar PDF</Button>
            <Button disabled={busy} onClick={() => run("whatsapp")} data-testid="btn-receipt-whatsapp" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"><WhatsappLogo size={16} /> WhatsApp</Button>
            <Button disabled={busy} variant="outline" onClick={() => run("email")} data-testid="btn-receipt-email" className="gap-1.5"><Envelope size={16} /> E-mail</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
